import { useMutation, useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { IconSend } from '@components/icons/send';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { api } from 'api';
import { Loading } from 'components/Loading';
import ShowMore from 'components/ShowMore';
import { Modal, ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';
import { IApplication, IJob } from 'models/types';

import { getNotificationText } from './utils';

type Props = ModalProps<{
  applicants: IApplication[];
  job: IJob;
}>;

export const HireflixBulkSendInterviewModal = observer((props: Props) => {
  const { closeModal, applicants, job } = props;
  const { t } = useStore();
  const { showError } = useToaster();

  const positionQuery = useQuery({
    queryKey: ['hireflixLinkedPosition'],
    queryFn: () => api.integrations.hireflixGetLinkedJobPosition(job.id),
  });

  const applicationIds = applicants.map((application) => application.id);
  const sendInterview = useMutation(
    () => api.integrations.hireflixBulkSendInterview(job.id, applicationIds),
    {
      onError: (error) => {
        if (api.global.isRequestError(error)) {
          showError(t(`common.errors.${error.response?.status}`));
        }
      },
    },
  );

  if (!positionQuery.isSuccess) {
    return <Loading />;
  }
  if (positionQuery.data && 'error' in positionQuery.data) {
    return (
      <Modal>
        <Modal.Header text={t('integrations.hireflix.bulk.modal.sent.header')} />
        <Text text={t('integrations.hireflix.bulk.modal.send.error')} />
        <Modal.Actions>
          <Button
            variant="secondary"
            onClick={closeModal}
            text={t('modals.confirm.button.close')}
          />
        </Modal.Actions>
      </Modal>
    );
  }

  if (sendInterview.isSuccess) {
    return (
      <Modal>
        <Modal.Header text={t('integrations.hireflix.bulk.modal.sent.header')} />
        <Text text={t('integrations.hireflix.bulk.modal.sent.text')} />
        <Modal.Actions>
          <Button
            variant="secondary"
            onClick={closeModal}
            text={t('modals.confirm.button.close')}
          />
        </Modal.Actions>
      </Modal>
    );
  }

  return (
    <Modal>
      <Modal.Header text={t('integrations.hireflix.application.modal.header')} />
      <Text text={t('integrations.hireflix.bulk.modal.send.header')} />
      <ShowMore
        max={applicants.length}
        count={applicants.length > 15 ? 10 : applicants.length}
        inc={1000}
        render={({ count }: { count: number }) => (
          <div>
            {applicants.slice(0, count).map((a, i) => (
              <Text font="bold16" key={i} text={a.name} />
            ))}
          </div>
        )}
      />
      <Stack space={1}>
        <Text
          font="semibold14"
          text={t('integrations.hireflix.application.modal.position.label')}
        />
        <Text font="regular16" text={positionQuery.data.name} />
      </Stack>
      {positionQuery.data.notifications && (
        <Stack space={1}>
          <Text
            font="semibold14"
            text={t('integrations.hireflix.application.modal.notifications.label')}
          />
          {positionQuery.data.notifications.map((notification, idx) => (
            <Text key={idx} font="regular16" text={getNotificationText(notification, t)} />
          ))}
        </Stack>
      )}
      <Modal.Actions>
        <Button
          variant="secondary"
          onClick={!sendInterview.isLoading ? closeModal : undefined}
          isDisabled={sendInterview.isLoading}
          text={t('modals.confirm.button.cancel')}
        />
        <Button
          variant="primary"
          onClick={!sendInterview.isLoading ? () => sendInterview.mutate() : undefined}
          isDisabled={sendInterview.isLoading}
          text={t('integrations.hireflix.application.modal.submit')}
          iconLeft={<IconSend />}
        />
      </Modal.Actions>
    </Modal>
  );
});
